import { Crown, Edit, Eye, Play, Shield, Wrench } from 'lucide-react';

/**
 * User role enumeration
 */
export enum UserRole {
	VIEWER = 'VIEWER',
	READ = 'READ',
	EXECUTE = 'EXECUTE',
	WRITE = 'WRITE',
	SUPPORT = 'SUPPORT',
	ADMIN = 'ADMIN',
}

/**
 * Role badge component with consistent styling across the app
 *
 * @description Displays user roles with appropriate colors, icons, and sizing.
 * Used throughout the application for consistent role visualization.
 */
export function RoleBadge({
	role,
	size = 'md',
	showIcon = false,
	showTooltip = true,
}: {
	role: UserRole | string;
	size?: 'xs' | 'sm' | 'md' | 'lg';
	showIcon?: boolean;
	showTooltip?: boolean;
}) {
	// Role color schemes
	const colors = {
		[UserRole.VIEWER]: {
			bg: 'bg-gray-100 text-gray-800 dark:bg-gray-800 dark:text-gray-200',
			border: 'border-gray-300 dark:border-gray-600',
			icon: Eye,
		},
		[UserRole.READ]: {
			bg: 'bg-blue-100 text-blue-800 dark:bg-blue-900 dark:text-blue-200',
			border: 'border-blue-300 dark:border-blue-600',
			icon: Eye,
		},
		[UserRole.EXECUTE]: {
			bg: 'bg-green-100 text-green-800 dark:bg-green-900 dark:text-green-200',
			border: 'border-green-300 dark:border-green-600',
			icon: Play,
		},
		[UserRole.WRITE]: {
			bg: 'bg-purple-100 text-purple-800 dark:bg-purple-900 dark:text-purple-200',
			border: 'border-purple-300 dark:border-purple-600',
			icon: Edit,
		},
		[UserRole.SUPPORT]: {
			bg: 'bg-yellow-100 text-yellow-800 dark:bg-yellow-900 dark:text-yellow-200',
			border: 'border-yellow-300 dark:border-yellow-600',
			icon: Wrench,
		},
		[UserRole.ADMIN]: {
			bg: 'bg-red-100 text-red-800 dark:bg-red-900 dark:text-red-200',
			border: 'border-red-300 dark:border-red-600',
			icon: Crown,
		},
	};

	// Size configurations
	const sizes = {
		xs: {
			text: 'text-xs',
			padding: 'px-2 py-0.5',
			icon: 'w-3 h-3',
		},
		sm: {
			text: 'text-xs',
			padding: 'px-2 py-1',
			icon: 'w-3 h-3',
		},
		md: {
			text: 'text-sm',
			padding: 'px-3 py-1',
			icon: 'w-4 h-4',
		},
		lg: {
			text: 'text-base',
			padding: 'px-4 py-2',
			icon: 'w-5 h-5',
		},
	};

	// Role descriptions for tooltips
	const descriptions = {
		[UserRole.VIEWER]: 'Limited access to assigned content only',
		[UserRole.READ]: 'View all workflows, executions, and folders',
		[UserRole.EXECUTE]: 'READ + trigger workflows and retry executions',
		[UserRole.WRITE]: 'EXECUTE + create, edit workflows and folders',
		[UserRole.SUPPORT]: 'READ + troubleshoot, view detailed logs, retry executions',
		[UserRole.ADMIN]: 'Full access including user management and system administration',
	};

	const roleKey = role as UserRole;
	const config = colors[roleKey] || colors[UserRole.READ]; // Fallback to READ
	const sizeConfig = sizes[size];
	const Icon = config.icon;
	const description = descriptions[roleKey] || 'Unknown role';

	const badgeContent = (
		<span
			className={`
        inline-flex items-center rounded-full font-medium border
        ${config.bg} ${config.border} ${sizeConfig.text} ${sizeConfig.padding}
        transition-colors duration-200
      `}
		>
			{showIcon && <Icon className={`mr-1 ${sizeConfig.icon}`} />}
			{role}
		</span>
	);

	if (showTooltip) {
		return (
			<div className="group relative inline-block">
				{badgeContent}
				<div className="invisible group-hover:visible absolute z-10 w-64 p-2 mt-1 text-sm text-white bg-gray-900 rounded-lg shadow-lg dark:bg-gray-700 -translate-x-1/2 left-1/2">
					<div className="font-medium mb-1">{role} Role</div>
					<div className="text-gray-300">{description}</div>
					<div className="absolute -top-1 left-1/2 w-2 h-2 bg-gray-900 rotate-45 transform -translate-x-1/2 dark:bg-gray-700"></div>
				</div>
			</div>
		);
	}

	return badgeContent;
}

/**
 * Role capability indicator component
 * Shows what a user can do with their current role
 */
export function RoleCapabilities({ role }: { role: UserRole | string }) {
	const capabilities = {
		[UserRole.VIEWER]: ['View assigned workflows', 'View own execution history'],
		[UserRole.READ]: [
			'View all workflows and executions',
			'Browse folders and content',
			'Access execution history',
		],
		[UserRole.EXECUTE]: [
			'All READ capabilities',
			'Trigger workflow executions',
			'Retry failed executions',
			'View execution details',
		],
		[UserRole.WRITE]: [
			'All EXECUTE capabilities',
			'Create and edit workflows',
			'Manage folders and tags',
			'Publish workflows',
		],
		[UserRole.SUPPORT]: [
			'All READ capabilities',
			'Troubleshoot executions',
			'View detailed logs',
			'Retry and cancel executions',
		],
		[UserRole.ADMIN]: [
			'All system capabilities',
			'Manage users and roles',
			'Delete workflows and folders',
			'Access admin dashboard',
			'View audit logs',
		],
	};

	const roleKey = role as UserRole;
	const userCapabilities = capabilities[roleKey] || [];

	return (
		<div className="bg-white dark:bg-gray-800 rounded-lg border border-gray-200 dark:border-gray-700 p-4">
			<div className="flex items-center mb-3">
				<RoleBadge role={role} showIcon={true} showTooltip={false} />
				<span className="ml-2 text-sm font-medium text-gray-900 dark:text-white">
					Capabilities
				</span>
			</div>

			<ul className="space-y-2">
				{userCapabilities.map((capability, index) => (
					<li
						key={index}
						className="flex items-center text-sm text-gray-600 dark:text-gray-400"
					>
						<div className="w-1.5 h-1.5 bg-green-500 rounded-full mr-2 flex-shrink-0"></div>
						{capability}
					</li>
				))}
			</ul>
		</div>
	);
}

/**
 * Role upgrade prompt component
 * Suggests role upgrades when users try to access restricted features
 */
export function RoleUpgradePrompt({
	currentRole,
	requiredRole,
	action,
	onRequestAccess,
}: {
	currentRole: UserRole | string;
	requiredRole: UserRole | string;
	action: string;
	onRequestAccess?: () => void;
}) {
	return (
		<div className="bg-yellow-50 dark:bg-yellow-900/20 border border-yellow-200 dark:border-yellow-800 rounded-lg p-4">
			<div className="flex items-start">
				<div className="flex-shrink-0">
					<Shield className="h-5 w-5 text-yellow-600 dark:text-yellow-400" />
				</div>
				<div className="ml-3 flex-1">
					<h3 className="text-sm font-medium text-yellow-800 dark:text-yellow-200">
						Upgrade Required
					</h3>
					<div className="mt-2 text-sm text-yellow-700 dark:text-yellow-300">
						<p>
							To {action}, you need{' '}
							<RoleBadge role={requiredRole} size="sm" showTooltip={false} /> role.
							You currently have{' '}
							<RoleBadge role={currentRole} size="sm" showTooltip={false} /> role.
						</p>
					</div>
					{onRequestAccess && (
						<div className="mt-3">
							<button
								onClick={onRequestAccess}
								className="text-sm bg-yellow-600 text-white px-3 py-1 rounded-md hover:bg-yellow-700 transition-colors"
							>
								Request Access
							</button>
						</div>
					)}
				</div>
			</div>
		</div>
	);
}
